import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import TabelaMetasPorPessoa from '@/components/metas/TabelaMetasPorPessoa';
import { METAS_PESSOA_DIA_VAZIA, METAS_PESSOA_VAZIA } from '@/metas/calculo';
import { salvarMetasPorSdr } from '@/services/metasService';

/**
 * ★★★ A-7a — AS METAS INDIVIDUAIS, agora em Indicadores de Performance.
 *
 * ★ É a tela do "por pessoa": a grade mensal por pessoa está aqui, então a meta de cada
 * pessoa passou a ser digitada aqui também. ⚠ O diálogo de `/metas`, que fazia as duas coisas
 * num formulário só, saiu do caminho (a grade do Dashboard ficou com as metas GERAIS).
 *
 * ★★★ ELE GRAVA SÓ `metasPorSdr`, e isso é a metade que importa: `salvarMetasPorSdr` usa
 * `merge: true` e **não toca** nas metas gerais nem em `diasUteis`. ⚠⚠ Com o gravador antigo
 * (`setDoc` sem `merge`) salvar aqui apagaria a meta de vendas do mês — sem aviso nenhum.
 *
 * ★★ SÓ GESTOR. ⚠ A trava é de quem MONTA (`{isGestor && …}` em `IndicadoresPage`): a página
 * é aberta à SDR de propósito, para ela ver a própria linha, e um diálogo de meta da equipe
 * dentro dela seria exatamente o vazamento que a tela evita no resto. ★ E as rules recusam a
 * escrita de qualquer forma — a tela é a primeira camada, não a única.
 */
export default function DialogoMetasIndividuais({ aberto, aoFechar, clinicaId, mes, metas, sdrs = [] }) {
  const [porSdr, setPorSdr] = useState(() => metas?.metasPorSdr ?? {});
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (aberto) {
      setPorSdr(metas?.metasPorSdr ?? {});
      setErro(null);
    }
  }, [aberto, metas]);

  /**
   * ★★★ Espalhar as DUAS famílias vazias, e não só uma: sem isso, `agendamentosDia` ficaria
   * fora do objeto de quem nunca o digitou — ⚠ e a chave só nasceria ao ser tocada, o que
   * torna "vazio" indistinguível de "ausente" para quem lê o documento depois.
   */
  function mudar(uid, campo, valor) {
    setPorSdr((d) => ({
      ...d,
      [uid]: {
        ...METAS_PESSOA_VAZIA,
        ...METAS_PESSOA_DIA_VAZIA,
        ...d?.[uid],
        [campo]: valor,
      },
    }));
  }

  async function salvar(evento) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await salvarMetasPorSdr(clinicaId, mes, porSdr);
      aoFechar();
    } catch (e) {
      setErro(
        e.code === 'permission-denied'
          ? 'Apenas o gestor da clínica pode definir metas.'
          : (e.message ?? 'Não foi possível salvar as metas individuais.'),
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialogo titulo={`Metas individuais de ${mes}`} aberto={aberto} aoFechar={aoFechar} largura={720}>
      <form className="formulario" onSubmit={salvar}>
        {sdrs.length === 0 ? (
          <p className="campo-ajuda">
            Ninguém da equipe exerce função de venda neste momento — não há meta individual a
            definir. As funções são configuradas em Equipe.
          </p>
        ) : (
          <TabelaMetasPorPessoa sdrs={sdrs} metasPorSdr={porSdr} aoMudar={mudar} />
        )}

        <p className="campo-ajuda">
          As metas GERAIS da clínica (vendas, ticket médio e as taxas) são digitadas na{' '}
          <strong>grade diária, no fim do Dashboard</strong>. Salvar aqui não as altera.
        </p>

        {erro && <p className="erro" role="alert">{erro}</p>}

        <footer className="dialogo-acoes">
          <button type="button" className="btn-secundario" onClick={aoFechar}>Cancelar</button>
          <button type="submit" className="btn-primario" disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar metas individuais'}
          </button>
        </footer>
      </form>
    </Dialogo>
  );
}
